import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import logo from '../images/dehaalogo-trim.png';

const PRODUCT_KEYS = [
  { key: 'realestate', href: '/demos/realestate/index.html' },
  { key: 'ecommerce', href: '/demos/ecommerce/index.html' },
  { key: 'medical', href: '/demos/medical/index.html' },
  { key: 'restaurant', href: '/demos/restaurant/index.html' },
];

const Header: React.FC = () => {
  const { t, language, setLanguage } = useLanguage();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  useEffect(() => {
    document.body.classList.toggle('nav-open', mobileOpen);
    return () => document.body.classList.remove('nav-open');
  }, [mobileOpen]);

  const close = () => setMobileOpen(false);

  return (
    <>
      <a className="skip-link" href="#main">Skip to content</a>
      <nav className={`site-nav ${scrolled ? 'is-stuck' : ''}`} aria-label="Primary">
        <div className="nav-inner">
          <Link className="nav-logo" to="/" aria-label="Dehaa Technology — home">
            <img src={logo} alt="Dehaa Technology" width={220} height={64} style={{ objectFit: 'contain', width: 'auto', height: '100%' }} />
          </Link>

          <ul className="nav-links">
            {/* Company */}
            <li>
              <Link className="top-link" to="/about">{t('header.nav.about') || 'Company'}<span className="nav-arrow" aria-hidden="true">▼</span></Link>
              <div className="mega"><div className="mega-inner">
                <Link className="mega-feature" to="/about">
                  <span className="tag">Since 2024 · Riyadh</span>
                  <span className="ttl">A Saudi-rooted digital studio</span>
                  <span className="explore">Explore <span aria-hidden="true">→</span></span>
                </Link>
                <div className="mega-cols">
                  <div className="mega-col"><h4>About</h4>
                    <Link to="/about">{t('header.nav.about')}</Link>
                    <Link to="/process">{t('header.nav.process')}</Link>
                    <Link to="/industries">{t('header.nav.industries')}</Link>
                    <Link to="/faq">{t('header.nav.faq')}</Link>
                  </div>
                  <div className="mega-col"><h4>More</h4>
                    <Link to="/testimonials">{t('header.nav.testimonials')}</Link>
                    <Link to="/portfolio">{t('header.nav.portfolio')}</Link>
                    <Link to="/pricing">{t('header.nav.pricing')}</Link>
                    <Link to="/contact">{t('header.nav.contact')}</Link>
                  </div>
                </div>
              </div></div>
            </li>

            {/* Portfolio */}
            <li>
              <Link className="top-link" to="/portfolio">{t('header.nav.portfolio') || 'Portfolio'}<span className="nav-arrow" aria-hidden="true">▼</span></Link>
              <div className="mega"><div className="mega-inner">
                <Link className="mega-feature" to="/portfolio">
                  <span className="tag">Our Work</span>
                  <span className="ttl">Projects shipped across the Kingdom</span>
                  <span className="explore">Explore <span aria-hidden="true">→</span></span>
                </Link>
                <div className="mega-cols">
                  <div className="mega-col"><h4>Showcase</h4>
                    <Link to="/portfolio">Web Portfolio</Link>
                    <Link to="/portfolio">Selected Work</Link>
                    <a href="/demos/ecommerce/index.html">Technologies</a>
                  </div>
                  <div className="mega-col"><h4>Sectors</h4>
                    <Link to="/industries">Industries</Link>
                    <Link to="/services">Website Design</Link>
                    <a href="/demos/realestate/index.html">Real Estate Demo</a>
                    <a href="/demos/medical/index.html">Medical Demo</a>
                  </div>
                </div>
              </div></div>
            </li>

            {/* Services */}
            <li>
              <Link className="top-link" to="/services">{t('header.nav.services') || 'Services'}<span className="nav-arrow" aria-hidden="true">▼</span></Link>
              <div className="mega"><div className="mega-inner">
                <Link className="mega-feature" to="/services">
                  <span className="tag">Build & Grow</span>
                  <span className="ttl">High-performance websites & search</span>
                  <span className="explore">Explore <span aria-hidden="true">→</span></span>
                </Link>
                <div className="mega-cols">
                  <div className="mega-col"><h4>Build</h4>
                    <Link to="/services">Web Design</Link>
                    <Link to="/services">Web Development</Link>
                    <Link to="/services">E-Commerce</Link>
                    <Link to="/services">AI Solutions</Link>
                    <a href="/demos/restaurant/index.html">Restaurant Demo</a>
                  </div>
                  <div className="mega-col"><h4>Grow</h4>
                    <Link to="/services">SEO</Link>
                    <Link to="/services">Branding</Link>
                    <Link to="/pricing">Pricing</Link>
                    <Link to="/contact">Maintenance</Link>
                  </div>
                </div>
              </div></div>
            </li>

            {/* Products - now proper Link like others */}
            <li>
              <Link className="top-link" to="/products">{t('header.nav.products')}<span className="nav-arrow" aria-hidden="true">▼</span></Link>
              <div className="mega"><div className="mega-inner">
                <Link className="mega-feature" to="/products">
                  <span className="tag">Products</span>
                  <span className="ttl">Ready-to-launch demos</span>
                  <span className="explore">Explore <span aria-hidden="true">→</span></span>
                </Link>
                <div className="mega-cols">
                  <div className="mega-col"><h4>Demos</h4>
                    {PRODUCT_KEYS.map(p => (
                      <Link key={p.key} to={`/product/${p.key}`} onClick={() => setMobileOpen(false)}>{t(`products.${p.key}`)}</Link>
                    ))}
                  </div>
                  <div className="mega-col"><h4>Live Demos</h4>
                    <a href="/demos/realestate/index.html" target="_blank" rel="noopener noreferrer">Real Estate Demo ↗</a>
                    <a href="/demos/ecommerce/index.html" target="_blank" rel="noopener noreferrer">E-Commerce Demo ↗</a>
                    <a href="/demos/medical/index.html" target="_blank" rel="noopener noreferrer">Medical Demo ↗</a>
                    <a href="/demos/restaurant/index.html" target="_blank" rel="noopener noreferrer">Restaurant Demo ↗</a>
                  </div>
                </div>
              </div></div>
            </li>

            <li><Link className="top-link" to="/pricing">{t('header.nav.pricing') || 'Pricing'}</Link></li>
          </ul>

          <div className="nav-cta">
            <div className="lang-switch">
              <a href="#" className={language === 'en' ? 'is-active' : ''} aria-current={language === 'en' ? 'true' : undefined} onClick={(e) => { e.preventDefault(); setLanguage('en'); }}>EN</a>
              <a href="#" className={language === 'ar' ? 'is-active' : ''} hrefLang="ar" lang="ar" onClick={(e) => { e.preventDefault(); setLanguage('ar'); }}>ع</a>
            </div>
            <Link className="btn btn-dark btn-sm" to="/contact">Start a Project <span className="ar" aria-hidden="true">→</span></Link>
            <button className="burger" data-burger aria-label="Open menu" aria-expanded={mobileOpen} onClick={() => setMobileOpen(v => !v)}>
              <span></span><span></span><span></span>
            </button>
          </div>
        </div>
      </nav>

      <div className={`mobile-nav ${mobileOpen ? 'open' : ''}`} data-mobile-nav>
        <button className="close-x" data-mobile-close aria-label="Close menu" onClick={close}>×</button>
        <Link to="/" onClick={close}>{t('header.nav.home')}</Link>
        <Link to="/about" onClick={close}>{t('header.nav.about')}</Link>
        <Link to="/services" onClick={close}>{t('header.nav.services')}</Link>
        <a href="/demos/ecommerce/index.html" onClick={close}>{t('header.nav.products')}</a>
        <Link to="/portfolio" onClick={close}>{t('header.nav.portfolio')}</Link>
        <Link to="/industries" onClick={close}>{t('header.nav.industries')}</Link>
        <Link to="/pricing" onClick={close}>{t('header.nav.pricing')}</Link>
        <Link to="/process" onClick={close}>{t('header.nav.process')}</Link>
        <Link to="/faq" onClick={close}>{t('header.nav.faq')}</Link>
        <Link to="/contact" onClick={close}>{t('header.nav.contact')}</Link>
        <div className="lang-switch">
          <a href="#" className={language === 'en' ? 'is-active' : ''} onClick={(e) => { e.preventDefault(); setLanguage('en'); close(); }}>English</a>
          <a href="#" className={language === 'ar' ? 'is-active' : ''} hrefLang="ar" onClick={(e) => { e.preventDefault(); setLanguage('ar'); close(); }}>العربية</a>
        </div>
      </div>
    </>
  );
};

export default Header;
